import type { BackgroundCanvasStatus } from "./types.js";
import type {
  AIAppId,
  AppProfileConfiguration,
  ActiveConversationResult,
  AssistantMessageDelivery,
  AttachedDocumentDeletePreview,
  Conversation,
  ConversationProfileConfiguration,
  ConversationSpecialistSettings,
  SolveResponseStyle,
  SolveResponseStyleState,
  SolveWebSearchState,
  ConversationAttachmentEnvelope,
  ConversationAttachmentPage,
  ConversationAttachmentUploadEnvelope,
  ContextualPrivateWorkspaceType,
  ConversationHydration,
  ConversationPage,
  ConnectionState,
  DocumentDownloadSession,
  GroupSettingsEnvelope,
  InteractionContext,
  MentionIndex,
  MessageEnvelope,
  MessageCommand,
  MessageMention,
  MessageFeedback,
  MessageFeedbackEnvelope,
  MessagePage,
  MyFilePage,
  ModelPolicyId,
  ModelPolicySummary,
  PlayerAIEvent,
  PlatformActionBatch,
  PlatformActionBatchEnvelope,
  CurrentPlatformActionBatchEnvelope,
  ProblemDetails,
  ProposalBatchDecisionEnvelope,
  ProposalBatchDecisionItem,
  ProposalDecisionEnvelope,
  ProposalDecisionSelection,
  ProposalRevisionInput,
  ProposalPage,
  RunEnvelope,
  SpecialistSettings,
  TodayConversationEnvelope,
  TypingEnvelope,
  UpdateGroupSettingsInput,
  UpdateSpecialistSettingsInput,
  UserSettings,
  UserSettingsPatch,
  ContextCompactionResult,
  EventPollReason,
  EventPollReply,
  PersonalProfileCatalogue,
  UpsertMessageFeedbackInput,
  UpsertPlatformActionBatchInput,
  WorkspaceType,
  HoshinSurfaceActionId,
  HoshinSurfaceActionPreview,
} from "./types.js";

export interface PlayerAIClientOptions {
  baseUrl: string;
  reconnectBaseMs?: number;
  getCsrfToken?: () => string | null | undefined;
  fetchImpl?: typeof fetch;
  /**
   * Fall back to the long poll when the live line is PROVEN broken (d1, d3).
   *
   * Off by default. While it is off the poll route is never called: a line
   * that said hello and then went quiet reconnects instead (d22).
   *
   * A boolean is fixed for the client's life. A getter is asked again at EVERY
   * fallback decision — the 5 s hello timer, the send check, the quiet check —
   * so a value that arrives after the live line opened still applies (the
   * shell passes the tenant's setting over the deployment's `/config.js`
   * default, d33). A getter that throws counts as off.
   */
  pollFallback?: boolean | (() => boolean);
}

/**
 * What a conversation's event transport reports to its host.
 *
 * `onActivity` fires for any byte on the live line and for any poll reply, so
 * a host can tell a silent transport from a quiet conversation (d26).
 * `onAccessQuestion` fires when a 403 or 404 ended both transports: the host
 * re-checks access and, only if it is confirmed, calls `restart()` (d25).
 */
export interface PlayerAIEventHandlers {
  onEvent: (event: PlayerAIEvent) => void;
  onState: (state: ConnectionState) => void;
  onError?: (error: Error) => void;
  onReconnectAttempt?: (attempt: number) => void;
  onActivity?: () => void;
  onAccessQuestion?: (error: Error) => void;
}

/**
 * A conversation's event transport. Calling it closes the transport for good,
 * exactly as the plain disposer it used to be.
 */
export interface PlayerAIEventConnection {
  (): void;
  /**
   * The send check (d3): a Send reply's `event_cursor` must reach the live
   * line within 5 s, or the transport falls back to the poll. A no-op while
   * `pollFallback` is off, once polling, or for a cursor already delivered;
   * `pollFallback` is asked again when the 5 s run out.
   */
  expectEvent(cursor: number): void;
  /**
   * Restart a transport that a 403 or 404 ended, live line first (d25).
   * Allowed ONCE per connection; answers whether it restarted.
   */
  restart(): boolean;
}

/** The live line's first frame, sent with no id and `data: {}` (d15). */
const STREAM_READY_EVENT = "stream.ready";
/** No hello this long after the fetch STARTED: held headers count too (n2). */
const HELLO_TIMEOUT_MS = 5_000;
/** How long a Send reply's cursor may take to arrive on the live line (d3). */
const SEND_CHECK_TIMEOUT_MS = 5_000;
/** About two missed heartbeats; the server sends one every 10.5–15 s. */
const QUIET_LINE_MS = 30_000;
/** One poll waits at most 20 s on the server; past this it is stuck. */
const POLL_TIMEOUT_MS = 30_000;
/** The least time between two EARLY empty poll replies (tight-loop guard). */
const POLL_EARLY_EMPTY_GAP_MS = 250;
const MAX_RECONNECT_DELAY_MS = 30_000;
/** No reply to a Send in this long surfaces as a failure (AC-13). */
const SEND_TIMEOUT_MS = 30_000;
/** Answers that end both transports instead of retrying them (d25). */
const ENDING_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);

interface PlayerAIRequestOptions {
  method?: string;
  body?: Record<string, unknown>;
  signal?: AbortSignal;
  /**
   * Bound on the whole request, body included. Only the two Send methods set
   * it: a mutation that legitimately runs longer (compaction, a document, an
   * applied proposal) must not be reported as an unknown write outcome.
   */
  timeoutMs?: number;
}

export class PlayerAIProblem extends Error {
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail || problem.title);
    this.name = "PlayerAIProblem";
    this.problem = problem;
  }
}

export class PlayerAIClient {
  private settingsListeners = new Set<(settings: UserSettings) => void>();
  private settingsSnapshot: UserSettings | null = null;
  private settingsQueue: Promise<unknown> = Promise.resolve();
  subscribeSettings = (
    listener: (settings: UserSettings) => void,
  ): (() => void) => {
    this.settingsListeners.add(listener);
    return () => {
      this.settingsListeners.delete(listener);
    };
  };
  private publishSettings(settings: UserSettings): UserSettings {
    if (
      this.settingsSnapshot &&
      (settings.preference_version ?? 0) <
        (this.settingsSnapshot.preference_version ?? 0)
    )
      return this.settingsSnapshot;
    this.settingsSnapshot = settings;
    for (const listener of this.settingsListeners) listener(settings);
    return settings;
  }
  getPersonalProfiles(): Promise<PersonalProfileCatalogue> {
    return this.request("/settings/profiles");
  }

  private readonly baseUrl: string;
  private readonly reconnectBaseMs: number;
  private readonly getCsrfToken: (() => string | null | undefined) | undefined;
  private readonly fetchImpl: typeof fetch;
  /** Read at each fallback decision, never cached (see the option). */
  private readonly pollFallback: () => boolean;
  private eventSession: EventConnectionSession | null = null;

  constructor(options: PlayerAIClientOptions) {
    this.baseUrl = `${options.baseUrl.replace(/\/$/, "")}/v2`;
    this.reconnectBaseMs = options.reconnectBaseMs ?? 1_000;
    this.getCsrfToken = options.getCsrfToken;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    const pollFallback = options.pollFallback;
    this.pollFallback =
      typeof pollFallback === "function"
        ? () => {
            try {
              return pollFallback() === true;
            } catch {
              return false;
            }
          }
        : () => pollFallback === true;
  }

  createPrivateConversation(title = "New conversation"): Promise<Conversation> {
    return this.request<{ conversation: Conversation }>("/conversations", {
      method: "POST",
      body: { title },
    }).then((value) => value.conversation);
  }

  listPrivateConversations(
    options: { cursor?: string; limit?: number } = {},
  ): Promise<ConversationPage> {
    const query = new URLSearchParams({
      limit: String(options.limit ?? 50),
    });
    if (options.cursor) query.set("cursor", options.cursor);
    return this.request(`/conversations?${query.toString()}`);
  }

  resolveTodayConversation(
    timeZone: string,
  ): Promise<TodayConversationEnvelope> {
    return this.request("/conversations/today", {
      method: "PUT",
      body: { time_zone: timeZone },
    });
  }

  createWorkspacePrivateConversation(
    workspaceType: ContextualPrivateWorkspaceType,
    workspaceId: string,
    title = "New conversation",
  ): Promise<Conversation> {
    return this.request<{ conversation: Conversation }>(
      `/workspaces/${encodeURIComponent(workspaceType)}/${encodeURIComponent(workspaceId)}/private-conversations`,
      { method: "POST", body: { title } },
    ).then((value) => value.conversation);
  }

  listWorkspacePrivateConversations(
    workspaceType: ContextualPrivateWorkspaceType,
    workspaceId: string,
    options: { cursor?: string; limit?: number } = {},
  ): Promise<ConversationPage> {
    const query = new URLSearchParams({
      limit: String(options.limit ?? 50),
    });
    if (options.cursor) query.set("cursor", options.cursor);
    return this.request(
      `/workspaces/${encodeURIComponent(workspaceType)}/${encodeURIComponent(workspaceId)}/private-conversations?${query.toString()}`,
    );
  }

  /**
   * @deprecated Use `createWorkspacePrivateConversation("hoshin_workshop", …)`.
   *
   * Kept as a delegating wrapper rather than removed: `packages/**` treats a
   * breaking export change as needing an architecture decision, and the change
   * here is a WIDENING (Hoshin plans also host owner-private threads), not a
   * decision about the contract. Behaviour is identical to the removed
   * method — it was hardcoded to `hoshin_workshop`.
   */
  createHoshinWorkshopPrivateConversation(
    workspaceId: string,
    title = "New conversation",
  ): Promise<Conversation> {
    return this.createWorkspacePrivateConversation(
      "hoshin_workshop",
      workspaceId,
      title,
    );
  }

  /**
   * @deprecated Use `listWorkspacePrivateConversations("hoshin_workshop", …)`.
   * See the note on `createHoshinWorkshopPrivateConversation`.
   */
  listHoshinWorkshopPrivateConversations(
    workspaceId: string,
    options: { cursor?: string; limit?: number } = {},
  ): Promise<ConversationPage> {
    return this.listWorkspacePrivateConversations(
      "hoshin_workshop",
      workspaceId,
      options,
    );
  }

  resolveWorkspaceConversation(
    workspaceType: WorkspaceType,
    workspaceId: string,
  ): Promise<{ conversation: Conversation }> {
    return this.request(
      `/workspaces/${encodeURIComponent(workspaceType)}/${encodeURIComponent(workspaceId)}/conversation`,
      { method: "PUT" },
    );
  }

  /**
   * Open whichever conversation the SERVER says is active for this workspace.
   *
   * The browser asks rather than chooses. A VSM in preparation resolves to the
   * caller's own private thread; the same VSM once its workshop starts resolves
   * to the shared one. Idempotent — two tabs converge on one conversation.
   */
  resolveActiveConversation(
    workspaceType: WorkspaceType,
    workspaceId: string,
  ): Promise<ActiveConversationResult> {
    return this.request(
      `/workspaces/${encodeURIComponent(workspaceType)}/${encodeURIComponent(workspaceId)}/active-conversation`,
      { method: "PUT" },
    );
  }

  previewHoshinSurfaceAction(
    workspaceId: string,
    actionId: HoshinSurfaceActionId,
    signal?: AbortSignal,
  ): Promise<HoshinSurfaceActionPreview> {
    return this.request(
      `/workspaces/hoshin_workshop/${encodeURIComponent(workspaceId)}/surface-actions/${encodeURIComponent(actionId)}/preview`,
      { method: "POST", ...(signal ? { signal } : {}) },
    );
  }

  backgroundCanvasStatus(
    conversationId: string,
  ): Promise<BackgroundCanvasStatus> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/background-canvas-status`,
    );
  }

  hydrateConversation(conversationId: string): Promise<ConversationHydration> {
    return this.request(`/conversations/${encodeURIComponent(conversationId)}`);
  }

  getCurrentPlatformActionBatch(
    conversationId: string,
  ): Promise<PlatformActionBatch | null> {
    return this.request<CurrentPlatformActionBatchEnvelope>(
      `/conversations/${encodeURIComponent(conversationId)}/platform-action-batches/current`,
    ).then((value) => value.batch);
  }

  getPlatformActionBatch(
    conversationId: string,
    batchId: string,
  ): Promise<PlatformActionBatch> {
    return this.request<PlatformActionBatchEnvelope>(
      `/conversations/${encodeURIComponent(conversationId)}/platform-action-batches/${encodeURIComponent(batchId)}`,
    ).then((value) => value.batch);
  }

  upsertPlatformActionBatch(
    conversationId: string,
    input: UpsertPlatformActionBatchInput,
  ): Promise<PlatformActionBatch> {
    return this.request<PlatformActionBatchEnvelope>(
      `/conversations/${encodeURIComponent(conversationId)}/platform-action-batches`,
      {
        method: "POST",
        body: {
          client_action_id: input.client_action_id,
          ...(input.batch_id ? { batch_id: input.batch_id } : {}),
          ...(input.expected_revision !== undefined
            ? { expected_revision: input.expected_revision }
            : {}),
          actions: [...input.actions],
        },
      },
    ).then((value) => value.batch);
  }

  applyPlatformActionBatch(
    conversationId: string,
    batchId: string,
    input: { client_action_id: string; frozen_hash: string },
  ): Promise<PlatformActionBatch> {
    return this.request<PlatformActionBatchEnvelope>(
      `/conversations/${encodeURIComponent(conversationId)}/platform-action-batches/${encodeURIComponent(batchId)}/apply`,
      { method: "POST", body: input },
    ).then((value) => value.batch);
  }

  fetchMessages(
    conversationId: string,
    options: { limit?: number; beforeSequence?: number | null } = {},
  ): Promise<MessagePage> {
    const query = new URLSearchParams({
      limit: String(options.limit ?? 50),
    });
    if (options.beforeSequence != null) {
      query.set("before_sequence", String(options.beforeSequence));
    }
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/messages?${query.toString()}`,
    );
  }

  putMessageFeedback(
    conversationId: string,
    messageId: string,
    input: UpsertMessageFeedbackInput,
  ): Promise<MessageFeedback> {
    return this.request<MessageFeedbackEnvelope>(
      `/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/feedback`,
      {
        method: "PUT",
        body: {
          rating: input.rating,
          comment: input.comment ?? null,
        },
      },
    ).then((value) => value.feedback);
  }

  getDocumentSupport(conversationId: string): Promise<{ available: boolean }> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/document-support`,
    );
  }

  listAttachments(
    conversationId: string,
    limit?: number,
  ): Promise<ConversationAttachmentPage>;
  listAttachments(
    conversationId: string,
    options?: { limit?: number; cursor?: string; signal?: AbortSignal },
  ): Promise<ConversationAttachmentPage>;
  listAttachments(
    conversationId: string,
    limitOrOptions:
      | number
      | { limit?: number; cursor?: string; signal?: AbortSignal } = {},
  ): Promise<ConversationAttachmentPage> {
    const options =
      typeof limitOrOptions === "number"
        ? { limit: limitOrOptions }
        : limitOrOptions;
    const query = new URLSearchParams({ limit: String(options.limit ?? 50) });
    if (options.cursor) query.set("cursor", options.cursor);
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachments?${query.toString()}`,
      options.signal ? { signal: options.signal } : {},
    );
  }

  createAttachmentUpload(
    conversationId: string,
    input: {
      idempotencyKey: string;
      displayFilename: string;
      declaredMediaType?: string;
      declaredSizeBytes: number;
    },
  ): Promise<ConversationAttachmentUploadEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachment-uploads`,
      {
        method: "POST",
        body: {
          idempotency_key: input.idempotencyKey,
          display_filename: input.displayFilename,
          ...(input.declaredMediaType
            ? { declared_media_type: input.declaredMediaType }
            : {}),
          declared_size_bytes: input.declaredSizeBytes,
        },
      },
    );
  }

  completeAttachmentUpload(
    conversationId: string,
    attachmentId: string,
  ): Promise<ConversationAttachmentEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/complete`,
      { method: "POST" },
    );
  }

  abortAttachmentUpload(
    conversationId: string,
    attachmentId: string,
  ): Promise<void> {
    return this.requestVoid(
      `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/upload`,
      { method: "DELETE" },
    );
  }

  listMyFiles(
    conversationId: string,
    options: { cursor?: string; limit?: number; signal?: AbortSignal } = {},
  ): Promise<MyFilePage> {
    const query = new URLSearchParams({ limit: String(options.limit ?? 25) });
    if (options.cursor) query.set("cursor", options.cursor);
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/my-files?${query.toString()}`,
      options.signal ? { signal: options.signal } : {},
    );
  }

  reuseAttachment(
    conversationId: string,
    documentId: string,
    idempotencyKey: string,
  ): Promise<ConversationAttachmentEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachments`,
      {
        method: "POST",
        body: {
          document_id: documentId,
          idempotency_key: idempotencyKey,
        },
      },
    );
  }

  createAttachmentDownload(
    conversationId: string,
    attachmentId: string,
  ): Promise<DocumentDownloadSession> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/download`,
      { method: "POST" },
    );
  }

  retryAttachmentRetrieval(
    conversationId: string,
    attachmentId: string,
    idempotencyKey: string,
  ): Promise<ConversationAttachmentEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/retrieval/retry`,
      {
        method: "POST",
        body: { idempotency_key: idempotencyKey },
      },
    );
  }

  unlinkAttachment(
    conversationId: string,
    attachmentId: string,
  ): Promise<void> {
    return this.requestVoid(
      `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: "DELETE" },
    );
  }

  previewAttachmentDocumentDelete(
    conversationId: string,
    attachmentId: string,
  ): Promise<AttachedDocumentDeletePreview> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/delete-preview`,
    );
  }

  deleteAttachmentDocument(
    conversationId: string,
    attachmentId: string,
    expectedDeleteVersion: number,
  ): Promise<ConversationAttachmentEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/document`,
      {
        method: "DELETE",
        body: { expected_delete_version: expectedDeleteVersion },
      },
    );
  }

  postSharedMessage(
    conversationId: string,
    clientMessageId: string,
    text: string,
    interactionContext?: InteractionContext,
    attachmentIds: readonly string[] = [],
    mentions: readonly MessageMention[] = [],
    commands: readonly MessageCommand[] = [],
    replyToMessageId?: string | null,
  ): Promise<MessageEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: "POST",
        body: {
          client_message_id: clientMessageId,
          text,
          ...(attachmentIds.length > 0
            ? { attachment_ids: attachmentIds }
            : {}),
          ...(mentions.length > 0 ? { mentions } : {}),
          ...(commands.length > 0 ? { commands } : {}),
          ...(replyToMessageId
            ? { reply_to_message_id: replyToMessageId }
            : {}),
          ...(interactionContext
            ? { interaction_context: interactionContext }
            : {}),
        },
        timeoutMs: SEND_TIMEOUT_MS,
      },
    );
  }

  /** Stop a Run (Task 14). Idempotent; the server re-checks who may. */
  stopRun(conversationId: string, runId: string): Promise<RunEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/runs/${encodeURIComponent(runId)}/stop`,
      { method: "POST" },
    );
  }

  /** One new Run for everything that waited while a group was stopped. */
  resumePendingRequests(
    conversationId: string,
    pausedRunId: string | null,
  ): Promise<GroupSettingsEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/pending-requests/resume`,
      { method: "POST", body: { paused_run_id: pausedRunId } },
    );
  }

  /**
   * Clear the requests the caller was shown, up to `throughMessageSequence`.
   * Their messages stay in the conversation and in later Runs' context.
   */
  discardPendingRequests(
    conversationId: string,
    pausedRunId: string | null,
    throughMessageSequence: number,
  ): Promise<GroupSettingsEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/pending-requests/discard`,
      {
        method: "POST",
        body: {
          paused_run_id: pausedRunId,
          through_message_sequence: throughMessageSequence,
        },
      },
    );
  }

  /** Messages that mention the caller, across the whole conversation (Task 6). */
  getMyMentions(conversationId: string): Promise<MentionIndex> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/mentions/me`,
    );
  }

  /** One tab's typing state in a group conversation (Task 15). */
  signalTyping(
    conversationId: string,
    input: { state: "typing" | "idle"; sessionId: string },
  ): Promise<void> {
    return this.requestVoid(
      `/conversations/${encodeURIComponent(conversationId)}/typing`,
      {
        method: "POST",
        body: { state: input.state, session_id: input.sessionId },
      },
    );
  }

  /** Who is typing now: what a (re)connecting client starts from. */
  getTyping(conversationId: string): Promise<TypingEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/typing`,
    );
  }

  getGroupSettings(conversationId: string): Promise<GroupSettingsEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/group-settings`,
    );
  }

  updateGroupSettings(
    conversationId: string,
    input: UpdateGroupSettingsInput,
  ): Promise<GroupSettingsEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/group-settings`,
      { method: "PATCH", body: { ...input } },
    );
  }

  createMessageRun(
    conversationId: string,
    input: {
      clientActionId: string;
      clientMessageId: string;
      text: string;
      interactionContext?: InteractionContext;
      assistantMessageDelivery?: AssistantMessageDelivery;
      currentDate?: import("./types.js").BrowserCurrentDate;
      attachmentIds?: readonly string[];
      /** The Player AI message this one replies to (Task 7). */
      replyToMessageId?: string | null;
    },
  ): Promise<RunEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/runs`,
      {
        method: "POST",
        body: {
          client_action_id: input.clientActionId,
          ...(input.currentDate ? { current_date: input.currentDate } : {}),
          ...(input.assistantMessageDelivery
            ? {
                output_delivery: {
                  assistant_message: input.assistantMessageDelivery,
                },
              }
            : {}),
          trigger: {
            type: "message",
            client_message_id: input.clientMessageId,
            text: input.text,
            ...(input.attachmentIds?.length
              ? { attachment_ids: input.attachmentIds }
              : {}),
            ...(input.replyToMessageId
              ? { reply_to_message_id: input.replyToMessageId }
              : {}),
            ...(input.interactionContext
              ? { interaction_context: input.interactionContext }
              : {}),
          },
        },
        timeoutMs: SEND_TIMEOUT_MS,
      },
    );
  }

  createNextRun(
    conversationId: string,
    clientActionId: string,
    assistantMessageDelivery?: AssistantMessageDelivery,
    currentDate?: import("./types.js").BrowserCurrentDate,
  ): Promise<RunEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/runs`,
      {
        method: "POST",
        body: {
          client_action_id: clientActionId,
          ...(currentDate ? { current_date: currentDate } : {}),
          ...(assistantMessageDelivery
            ? {
                output_delivery: {
                  assistant_message: assistantMessageDelivery,
                },
              }
            : {}),
          trigger: { type: "next" },
        },
      },
    );
  }

  listProposals(
    conversationId: string,
    options: { cursor?: string; limit?: number } = {},
  ): Promise<ProposalPage> {
    const query = new URLSearchParams({
      limit: String(options.limit ?? 50),
    });
    if (options.cursor) query.set("cursor", options.cursor);
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/proposals?${query.toString()}`,
    );
  }

  decideProposal(
    conversationId: string,
    proposalId: string,
    input: {
      clientActionId: string;
      decision: "approve" | "reject";
      reason?: string;
      /**
       * The decider's bounded narrowing of an already-pinned command — today
       * only the Hoshin duplicate review, whose card lets the driver select
       * and reword which replacements to keep. Player AI validates it against
       * the pinned proposal input; it can never widen what was proposed.
       */
      selection?: ProposalDecisionSelection;
      /**
       * An edited value set, on `approve` only.
       *
       * Omitting it keeps the unchanged-approval path byte-identical, which
       * matters: that is the path every non-integration proposal in the
       * product still takes. Supplying it makes the server create an immutable
       * successor and re-prepare the write from scratch — so the response's
       * proposal is the SUCCESSOR, not the one that was decided on.
       */
      revision?: ProposalRevisionInput;
    },
  ): Promise<ProposalDecisionEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/proposals/${encodeURIComponent(proposalId)}/decision`,
      {
        method: "POST",
        body: {
          client_action_id: input.clientActionId,
          decision: input.decision,
          ...(input.reason ? { reason: input.reason } : {}),
          ...(input.selection ? { selection: input.selection } : {}),
          ...(input.revision ? { revision: input.revision } : {}),
        },
      },
    );
  }

  decideProposalBatch(
    conversationId: string,
    approvalBatchId: string,
    input: {
      clientActionId: string;
      items: ProposalBatchDecisionItem[];
    },
  ): Promise<ProposalBatchDecisionEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/proposal-batches/${encodeURIComponent(approvalBatchId)}/decision`,
      {
        method: "POST",
        body: {
          client_action_id: input.clientActionId,
          items: input.items.map((item) => ({
            proposal_id: item.proposalId,
            decision: item.decision,
            ...(item.reason ? { reason: item.reason } : {}),
            ...(item.revision ? { revision: item.revision } : {}),
          })),
        },
      },
    );
  }

  decideDocumentWait(
    conversationId: string,
    runId: string,
    input: {
      clientActionId: string;
      decision: "wait" | "continue";
    },
  ): Promise<RunEnvelope> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/runs/${encodeURIComponent(runId)}/document-wait-decision`,
      {
        method: "POST",
        body: {
          client_action_id: input.clientActionId,
          decision: input.decision,
        },
      },
    );
  }

  getAppProfile(appId: AIAppId): Promise<AppProfileConfiguration> {
    return this.request(`/settings/apps/${encodeURIComponent(appId)}/profile`);
  }

  getConversationProfile(
    conversationId: string,
  ): Promise<ConversationProfileConfiguration> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/profile`,
    );
  }

  updateConversationProfile(
    conversationId: string,
    profileId: string | null,
    expectedVersion: number,
  ): Promise<ConversationProfileConfiguration> {
    return this.request(
      `/conversations/${encodeURIComponent(conversationId)}/profile`,
      {
        method: "PATCH",
        body: { profile_id: profileId, expected_version: expectedVersion },
      },
    );
  }

  updateAppProfile(
    appId: AIAppId,
    profileId: string | null,
    expectedVersion: number,
  ): Promise<AppProfileConfiguration> {
    return this.request(`/settings/apps/${encodeURIComponent(appId)}/profile`, {
      method: "PUT",
      body: { profile_id: profileId, expected_version: expectedVersion },
    });
  }

  getSettings(): Promise<UserSettings> {
    return this.request<{ settings: UserSettings }>("/settings").then((value) =>
      this.publishSettings(value.settings),
    );
  }

  /**
   * "Try again" on a failed context compaction.
   *
   * Idempotent, and safe to press twice: the server publishes under a
   * compare-and-swap whose coverage only moves forward, so a retry racing the
   * automatic pass loses cleanly rather than overwriting it. A failure leaves
   * the previous valid checkpoint in force, so this can only improve the
   * thread's state or leave it exactly as it was.
   */
  compactConversationContext(
    conversationId: string,
  ): Promise<ContextCompactionResult> {
    return this.request<{ result: ContextCompactionResult }>(
      `/conversations/${encodeURIComponent(conversationId)}/context/compact`,
      { method: "POST" },
    ).then((value) => value.result);
  }

  listModelPolicies(): Promise<ModelPolicySummary[]> {
    return this.request<{ policies: ModelPolicySummary[] }>(
      "/model-policies",
    ).then((value) => value.policies);
  }

  updateSettings(patch: UserSettingsPatch): Promise<UserSettings> {
    const write = this.settingsQueue
      .catch(() => undefined)
      .then(() =>
        this.request<{ settings: UserSettings }>("/settings", {
          method: "PATCH",
          body: { ...patch },
        }),
      )
      .then((value) => this.publishSettings(value.settings));
    this.settingsQueue = write;
    return write;
  }

  getSpecialistSettings(
    module: "solve",
    specialistId: string,
  ): Promise<SpecialistSettings> {
    return this.request<{ settings: SpecialistSettings }>(
      `/modules/${encodeURIComponent(module)}/specialists/${encodeURIComponent(specialistId)}/settings`,
    ).then((value) => value.settings);
  }

  /** Tenant Admin only; the server enforces the role and the AIS policy check. */
  updateSpecialistSettings(
    module: "solve",
    specialistId: string,
    patch: UpdateSpecialistSettingsInput,
  ): Promise<SpecialistSettings> {
    return this.request<{ settings: SpecialistSettings }>(
      `/modules/${encodeURIComponent(module)}/specialists/${encodeURIComponent(specialistId)}/settings`,
      {
        method: "PATCH",
        body: { ...patch },
      },
    ).then((value) => value.settings);
  }

  getConversationSpecialistSettings(
    conversationId: string,
    specialistId: string,
  ): Promise<ConversationSpecialistSettings> {
    return this.request<{ settings: ConversationSpecialistSettings }>(
      `/conversations/${encodeURIComponent(conversationId)}/specialists/${encodeURIComponent(specialistId)}/settings`,
    ).then((value) => value.settings);
  }

  updateConversationSpecialistSettings(
    conversationId: string,
    specialistId: string,
    patch:
      | ModelPolicyId
      | null
      | {
          model_policy_id?: ModelPolicyId | null;
          profile_id?: string | null;
          expected_profile_version?: number;
        },
  ): Promise<ConversationSpecialistSettings> {
    return this.request<{ settings: ConversationSpecialistSettings }>(
      `/conversations/${encodeURIComponent(conversationId)}/specialists/${encodeURIComponent(specialistId)}/settings`,
      {
        method: "PATCH",
        body:
          typeof patch === "object" && patch !== null
            ? { ...patch }
            : { model_policy_id: patch },
      },
    ).then((value) => value.settings);
  }

  getSolveResponseStyle(
    conversationId: string,
  ): Promise<SolveResponseStyleState> {
    return this.request<SolveResponseStyleState>(
      `/conversations/${encodeURIComponent(conversationId)}/solve/response-style`,
    );
  }

  updateSolveResponseStyle(
    conversationId: string,
    responseStyle: SolveResponseStyle,
  ): Promise<SolveResponseStyleState> {
    return this.request<SolveResponseStyleState>(
      `/conversations/${encodeURIComponent(conversationId)}/solve/response-style`,
      {
        method: "PATCH",
        body: { response_style: responseStyle },
      },
    );
  }

  /** Player AI owns this preference for non-SOLVE conversations. */
  getWebSearch(conversationId: string): Promise<SolveWebSearchState> {
    return this.request<SolveWebSearchState>(
      `/conversations/${encodeURIComponent(conversationId)}/web-search`,
    );
  }

  /** Use updateSolveWebSearch for SOLVE's product-owned workspace preference. */
  updateWebSearch(
    conversationId: string,
    enabled: boolean,
  ): Promise<SolveWebSearchState> {
    return this.request<SolveWebSearchState>(
      `/conversations/${encodeURIComponent(conversationId)}/web-search`,
      { method: "PATCH", body: { enabled } },
    );
  }

  /** SOLVE reads its product-owned setting through this compatibility endpoint. */
  getSolveWebSearch(conversationId: string): Promise<SolveWebSearchState> {
    return this.request<SolveWebSearchState>(
      `/conversations/${encodeURIComponent(conversationId)}/solve/web-search`,
    );
  }

  /** SOLVE remains product-owned; this is the active SOLVE write path. */
  updateSolveWebSearch(
    conversationId: string,
    enabled: boolean,
  ): Promise<SolveWebSearchState> {
    return this.request<SolveWebSearchState>(
      `/conversations/${encodeURIComponent(conversationId)}/solve/web-search`,
      {
        method: "PATCH",
        body: { enabled },
      },
    );
  }

  /**
   * Open the conversation's event transport: the live line (SSE) first,
   * always, and the long poll only when `pollFallback` is on at the moment the
   * line is proven broken (d1, d3). See {@link EventConnectionSession}.
   */
  connectEvents(
    conversationId: string,
    afterSequence: number,
    handlers: PlayerAIEventHandlers,
  ): PlayerAIEventConnection {
    this.disconnect();
    const session = new EventConnectionSession(
      {
        eventsUrl: `${this.baseUrl}/conversations/${encodeURIComponent(conversationId)}/events`,
        fetchImpl: this.fetchImpl,
        reconnectBaseMs: this.reconnectBaseMs,
        pollFallback: this.pollFallback,
      },
      afterSequence,
      handlers,
    );
    this.eventSession = session;
    session.start();
    const dispose = (): void => {
      if (this.eventSession === session) this.eventSession = null;
      session.dispose();
    };
    return Object.assign(dispose, {
      expectEvent: (cursor: number) => session.expectEvent(cursor),
      restart: () => session.restart(),
    });
  }

  disconnect(): void {
    this.eventSession?.halt();
    this.eventSession = null;
  }

  private request<T>(
    path: string,
    options: PlayerAIRequestOptions = {},
  ): Promise<T> {
    return this.send(
      path,
      options,
      (response) => response.json() as Promise<T>,
    );
  }

  private async requestVoid(
    path: string,
    options: { method: string; body?: Record<string, unknown> },
  ): Promise<void> {
    await this.send(path, options, async () => undefined);
  }

  /**
   * One request; with `timeoutMs`, bounded at that including its body.
   *
   * A filter that holds the reply must not hold the composer with it: with no
   * bound, a Send that never answers keeps the person locked out with no
   * message (AC-13). The timeout rejects with a `TimeoutError`, which the
   * failure catalogue already classifies as a write whose outcome is unknown.
   * Only Sends are bounded ({@link SEND_TIMEOUT_MS}); every other request keeps
   * its earlier unbounded wait.
   *
   * The timer and the caller's signal are combined BY HAND, as in
   * `useVsmEntityHistory`: `AbortSignal.any` needs Safari 17.4, and a plain
   * timer is what fake-timer tests can drive. The read settles on the abort
   * itself, so a fetch that ignores its signal cannot outlive the bound.
   */
  private async send<T>(
    path: string,
    options: PlayerAIRequestOptions,
    read: (response: Response) => Promise<T>,
  ): Promise<T> {
    const method = options.method ?? "GET";
    const csrf = this.getCsrfToken?.();
    const request = new AbortController();
    const timer =
      options.timeoutMs === undefined
        ? undefined
        : setTimeout(
            () => request.abort(timeoutError("Player AI request timed out")),
            options.timeoutMs,
          );
    const caller = options.signal;
    const cancel = (): void => request.abort(caller?.reason);
    if (caller?.aborted) cancel();
    else caller?.addEventListener("abort", cancel, { once: true });
    try {
      return await settleOnAbort(
        request.signal,
        (async () => {
          const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
            method,
            credentials: "include",
            headers: {
              accept: "application/json",
              ...(options.body ? { "content-type": "application/json" } : {}),
              ...(csrf && method !== "GET" ? { "x-csrf-token": csrf } : {}),
            },
            ...(options.body ? { body: JSON.stringify(options.body) } : {}),
            signal: request.signal,
          });
          if (!response.ok) throw await problemFromResponse(response);
          return read(response);
        })(),
      );
    } finally {
      clearTimeout(timer);
      caller?.removeEventListener("abort", cancel);
    }
  }
}

/** Whether a Send failed because it hit {@link SEND_TIMEOUT_MS}. */
export function isPlayerAIRequestTimeout(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "TimeoutError"
  );
}

function timeoutError(message: string): Error {
  return new DOMException(message, "TimeoutError");
}

function settleOnAbort<T>(signal: AbortSignal, work: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const abort = (): void => reject(signal.reason);
    if (signal.aborted) abort();
    else signal.addEventListener("abort", abort, { once: true });
    work.then(resolve, reject).finally(() => {
      signal.removeEventListener("abort", abort);
    });
  });
}

type Timer = ReturnType<typeof setTimeout>;

type PollOutcome =
  | { readonly ok: true; readonly reply: ReturnType<typeof pollReply> }
  | { readonly ok: false; readonly status: number; readonly problem: Error };

/** One attempt of the live line, with the timers that judge it. */
interface LineAttempt {
  readonly controller: AbortController;
  /** `stream.ready` arrived on this attempt: bytes reach this browser. */
  proven: boolean;
  /** Aborted on purpose by the quiet check, to reconnect at once (d22). */
  quietReconnect: boolean;
  hello: Timer | null;
  quiet: Timer | null;
}

/**
 * One conversation's event transport for as long as the host keeps it open.
 *
 * The live line always goes first. It counts as proven only once its
 * `stream.ready` hello arrives, and the hello timer starts when the FETCH
 * starts, because a filter can hold the headers as well as the body (n2).
 *
 * `pollFallback` is a getter asked at each decision below, not a value read
 * once, so a setting that arrives after the line opened still applies.
 *
 * With `pollFallback` on, three proofs move the session to the long poll for
 * good (d3): no hello in 5 s (`no_hello`), a Send reply's cursor not seen in
 * 5 s (`send_check`), or about 30 s with no byte at all (`quiet`). The line is
 * closed FIRST, so a filter never holds two requests (n1), and the poll starts
 * from the cursor THIS transport delivered — never the host's, which a Send
 * reply can move past events the line never brought (m6).
 *
 * With it off the poll is never called (d22). A line that said hello and then
 * went quiet reconnects from its own cursor; a reconnect that cannot say hello
 * in 5 s is held, and a held line is never quiet-reconnected again, so a
 * filter cannot turn the check into a loop. Error backoff is unchanged.
 *
 * 401, 403 and 404 end both transports instead of retrying (d25). Every other
 * failure backs off 1, 2, 4… 30 s.
 */
class EventConnectionSession {
  private cursor: number;
  /** The running transport: aborting it ends its loop and its waits. */
  private run: AbortController | null = null;
  private line: LineAttempt | null = null;
  private request: AbortController | null = null;
  private readonly sendChecks = new Set<Timer>();
  private state: ConnectionState | null = null;
  private polling = false;
  private held = false;
  private ended = false;
  /** A 403 or 404 ended it: a question the host may answer with a restart. */
  private restartable = false;
  private restartUsed = false;
  private halted = false;

  constructor(
    private readonly config: {
      readonly eventsUrl: string;
      readonly fetchImpl: typeof fetch;
      readonly reconnectBaseMs: number;
      readonly pollFallback: () => boolean;
    },
    initialCursor: number,
    private readonly handlers: PlayerAIEventHandlers,
  ) {
    this.cursor = initialCursor;
  }

  start(): void {
    const run = new AbortController();
    this.run = run;
    void this.runLive(run.signal);
  }

  /** The host closed the transport: stop, and say so. */
  dispose(): void {
    this.halt();
    this.handlers.onReconnectAttempt?.(0);
    this.handlers.onState("closed");
  }

  /** Stop everything without telling the host (a newer session replaced it). */
  halt(): void {
    this.halted = true;
    this.stopTransport();
  }

  expectEvent(cursor: number): void {
    if (
      !this.config.pollFallback() ||
      this.halted ||
      this.ended ||
      this.polling ||
      !isCursor(cursor) ||
      cursor <= this.cursor
    ) {
      return;
    }
    const timer = setTimeout(() => {
      this.sendChecks.delete(timer);
      if (this.cursor < cursor) this.fallBack("send_check");
    }, SEND_CHECK_TIMEOUT_MS);
    this.sendChecks.add(timer);
  }

  restart(): boolean {
    if (this.halted || !this.restartable || this.restartUsed) return false;
    this.restartUsed = true;
    this.restartable = false;
    this.ended = false;
    this.polling = false;
    this.held = false;
    this.start();
    return true;
  }

  private async runLive(run: AbortSignal): Promise<void> {
    let failures = 0;
    let reconnecting = false;
    while (!run.aborted) {
      this.setState(reconnecting ? "reconnecting" : "connecting");
      const line: LineAttempt = {
        controller: new AbortController(),
        proven: false,
        quietReconnect: false,
        hello: null,
        quiet: null,
      };
      this.line = line;
      const isReconnect = reconnecting;
      line.hello = setTimeout(() => {
        line.hello = null;
        if (run.aborted || line.proven) return;
        if (this.config.pollFallback()) this.fallBack("no_hello");
        else if (isReconnect) this.held = true;
      }, HELLO_TIMEOUT_MS);
      try {
        const response = await this.config.fetchImpl(this.config.eventsUrl, {
          method: "GET",
          credentials: "include",
          headers: {
            accept: "text/event-stream",
            ...(this.cursor > 0
              ? { "last-event-id": String(this.cursor) }
              : {}),
          },
          signal: line.controller.signal,
        });
        if (!response.ok) {
          const problem = await problemFromResponse(response);
          if (ENDING_STATUSES.has(response.status)) {
            this.end(problem, response.status);
            return;
          }
          throw problem;
        }
        if (!response.body)
          throw new Error("Player AI event stream has no body");
        failures = 0;
        this.handlers.onReconnectAttempt?.(0);
        this.setState("open");
        this.armQuiet(run, line);
        await consumeSseBody(
          response.body,
          () => {
            this.handlers.onActivity?.();
            this.armQuiet(run, line);
          },
          (event) => this.receive(event, line),
          line.controller.signal,
        );
        if (!line.controller.signal.aborted) {
          throw new Error("Player AI event stream closed");
        }
      } catch (error) {
        if (run.aborted) return;
        if (!line.quietReconnect) {
          this.handlers.onError?.(asError(error));
          failures += 1;
          this.handlers.onReconnectAttempt?.(failures);
          this.setState("reconnecting");
        }
      } finally {
        clearLine(line);
      }
      if (run.aborted) return;
      reconnecting = true;
      if (line.quietReconnect) continue;
      await abortableDelay(this.reconnectDelay(failures), run);
    }
  }

  /** Re-armed by every chunk, so any byte — a heartbeat too — is activity. */
  private armQuiet(run: AbortSignal, line: LineAttempt): void {
    if (line.quiet !== null) clearTimeout(line.quiet);
    line.quiet = setTimeout(() => {
      line.quiet = null;
      if (run.aborted || line.controller.signal.aborted) return;
      if (this.config.pollFallback()) {
        this.fallBack("quiet");
        return;
      }
      // d22: only a line that said hello reconnects; a held one never again.
      if (!line.proven || this.held) return;
      line.quietReconnect = true;
      line.controller.abort();
    }, QUIET_LINE_MS);
  }

  private receive(event: PlayerAIEvent, line: LineAttempt): void {
    if (event.eventType === STREAM_READY_EVENT) {
      // The hello proves the line; it is not a conversation event, never
      // reaches the host and never moves the cursor.
      line.proven = true;
      if (line.hello !== null) clearTimeout(line.hello);
      line.hello = null;
      return;
    }
    this.deliver(event);
  }

  /** The seen-number guard both transports share: an id is applied once. */
  private deliver(event: PlayerAIEvent): void {
    if (event.id !== null) {
      if (event.id <= this.cursor) return;
      this.cursor = event.id;
    }
    this.handlers.onEvent(event);
  }

  private fallBack(reason: EventPollReason): void {
    if (
      !this.config.pollFallback() ||
      this.halted ||
      this.ended ||
      this.polling
    ) {
      return;
    }
    this.polling = true;
    // Close the line FIRST (n1), then poll from this transport's cursor.
    this.stopTransport();
    const run = new AbortController();
    this.run = run;
    void this.runPoll(run.signal, reason);
  }

  private async runPoll(
    run: AbortSignal,
    reason: EventPollReason,
  ): Promise<void> {
    let failures = 0;
    while (!run.aborted) {
      const request = new AbortController();
      this.request = request;
      const timer = setTimeout(
        () => request.abort(timeoutError("Player AI poll timed out")),
        POLL_TIMEOUT_MS,
      );
      const startedAt = Date.now();
      let empty: boolean;
      try {
        const query = new URLSearchParams({
          after: String(this.cursor),
          reason,
        });
        const outcome = await settleOnAbort<PollOutcome>(
          request.signal,
          (async () => {
            const response = await this.config.fetchImpl(
              `${this.config.eventsUrl}/poll?${query.toString()}`,
              {
                method: "GET",
                credentials: "include",
                headers: { accept: "application/json" },
                signal: request.signal,
              },
            );
            if (!response.ok) {
              return {
                ok: false,
                status: response.status,
                problem: await problemFromResponse(response),
              };
            }
            return { ok: true, reply: pollReply(await response.json()) };
          })(),
        );
        if (run.aborted) return;
        if (!outcome.ok) {
          if (ENDING_STATUSES.has(outcome.status)) {
            this.end(outcome.problem, outcome.status);
            return;
          }
          throw outcome.problem;
        }
        this.handlers.onActivity?.();
        for (const event of outcome.reply.events) this.deliver(event);
        this.cursor = Math.max(this.cursor, outcome.reply.cursor);
        empty = outcome.reply.events.length === 0;
        failures = 0;
        this.handlers.onReconnectAttempt?.(0);
        this.setState("open");
      } catch (error) {
        if (run.aborted) return;
        this.handlers.onError?.(asError(error));
        failures += 1;
        this.handlers.onReconnectAttempt?.(failures);
        this.setState("reconnecting");
        await abortableDelay(this.reconnectDelay(failures), run);
        continue;
      } finally {
        clearTimeout(timer);
      }
      // A reply with events is followed at once. An empty one that came back
      // EARLY — a proxy answering for the server, say — waits out the rest of
      // the guard, so a misbehaving hop cannot spin this loop.
      const elapsed = Date.now() - startedAt;
      if (empty && elapsed < POLL_EARLY_EMPTY_GAP_MS) {
        await abortableDelay(POLL_EARLY_EMPTY_GAP_MS - elapsed, run);
      }
    }
  }

  /** 401, 403 and 404 end both transports; only a 403 or 404 is a question. */
  private end(problem: Error, status: number): void {
    this.ended = true;
    this.restartable = status !== 401;
    this.stopTransport();
    this.handlers.onReconnectAttempt?.(0);
    this.setState("closed");
    this.handlers.onError?.(problem);
    if (status !== 401) this.handlers.onAccessQuestion?.(problem);
  }

  private stopTransport(): void {
    this.run?.abort();
    this.run = null;
    if (this.line) {
      clearLine(this.line);
      this.line.controller.abort();
      this.line = null;
    }
    this.request?.abort();
    this.request = null;
    for (const timer of this.sendChecks) clearTimeout(timer);
    this.sendChecks.clear();
  }

  private reconnectDelay(failures: number): number {
    return Math.min(
      MAX_RECONNECT_DELAY_MS,
      this.config.reconnectBaseMs * Math.pow(2, failures - 1),
    );
  }

  /** Repeats are dropped: a poll every few seconds is not a new "open". */
  private setState(state: ConnectionState): void {
    if (this.halted || this.state === state) return;
    this.state = state;
    this.handlers.onState(state);
  }
}

function clearLine(line: LineAttempt): void {
  if (line.hello !== null) clearTimeout(line.hello);
  if (line.quiet !== null) clearTimeout(line.quiet);
  line.hello = null;
  line.quiet = null;
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function isCursor(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

/**
 * One poll reply (d29), validated rather than trusted: `data` is the same
 * object the live line JSON-encodes, so a polled event reaches the host in
 * exactly the shape a streamed one does.
 */
function pollReply(value: unknown): {
  events: PlayerAIEvent[];
  cursor: number;
} {
  const reply = value as Partial<EventPollReply> | null;
  if (
    typeof reply !== "object" ||
    reply === null ||
    !Array.isArray(reply.events) ||
    !isCursor(reply.cursor)
  ) {
    throw new Error("Player AI poll reply is malformed");
  }
  return {
    cursor: reply.cursor,
    events: reply.events.map((candidate: unknown) => {
      const event = candidate as Partial<EventPollReply["events"][number]>;
      if (
        typeof event !== "object" ||
        event === null ||
        !isCursor(event.id) ||
        typeof event.event !== "string"
      ) {
        throw new Error("Player AI poll reply is malformed");
      }
      const data: unknown = event.data;
      return {
        id: event.id,
        eventType: event.event,
        data:
          typeof data === "object" && data !== null && !Array.isArray(data)
            ? (data as Record<string, unknown>)
            : { value: data },
      };
    }),
  };
}

/**
 * The code to assume when a response carries no problem+json envelope.
 *
 * The STATUS is authoritative even when the body is not: an HTML 401 from a
 * proxy still means the session is gone, and telling that reader "Player AI is
 * temporarily unavailable — try again in a moment" sends them round a loop no
 * retry can end. Only a status that says nothing specific falls through to the
 * unreadable-response code.
 */
function codeForStatus(status: number): string {
  if (status === 401) return "UNAUTHENTICATED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 429) return "RATE_LIMITED";
  if (status === 413) return "INPUT_TOO_LARGE";
  return "SERVICE_RESPONSE_UNREADABLE";
}

/**
 * PB-5276: a non-problem response contributes its STATUS, never its body.
 *
 * This used to append `await response.text()` to the message, and that message
 * was rendered verbatim in the rail. Anything between the browser and Player AI
 * can answer without the problem+json contract — a gateway timeout page, a
 * proxy's HTML, a WAF block — and all of it went straight onto the user's
 * screen.
 *
 * A malformed problem+json body takes the same path: the content type is a
 * claim, not a guarantee, and a parse failure must not become an unhandled
 * rejection inside the caller's `catch`.
 */
async function problemFromResponse(response: Response): Promise<Error> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/problem+json")) {
    try {
      const body = (await response.json()) as ProblemDetails;
      if (typeof body?.code === "string" && body.code.length > 0) {
        return new PlayerAIProblem({
          ...body,
          status: body.status ?? response.status,
        });
      }
    } catch {
      // Fall through to the status-derived envelope below.
    }
  }
  const requestId = response.headers.get("x-request-id");
  return new PlayerAIProblem({
    type: "/problems/service-response-unreadable",
    // The STATUS is kept in the thrown message — it is a protocol fact, not
    // content — so a developer log still says which upstream answered what.
    title: `Player AI request failed (${response.status})`,
    status: response.status,
    detail: "",
    code: codeForStatus(response.status),
    ...(requestId ? { request_id: requestId } : {}),
  });
}

/**
 * Read one live line until it ends or `signal` aborts.
 *
 * `onChunk` fires for every chunk, before it is parsed, so a heartbeat comment
 * counts as life exactly like an event does. An abort cancels the reader: a
 * pending read must not outlive the attempt that issued it.
 */
async function consumeSseBody(
  body: ReadableStream<Uint8Array>,
  onChunk: () => void,
  onEvent: (event: PlayerAIEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const cancel = (): void => {
    reader.cancel().catch(() => undefined);
  };
  signal.addEventListener("abort", cancel, { once: true });
  let buffer = "";
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (value !== undefined && value.byteLength > 0) onChunk();
      buffer += decoder.decode(value, { stream: !done });
      let boundary = eventBoundary(buffer);
      while (boundary) {
        const block = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary.length);
        const parsed = parseSseBlock(block);
        if (parsed) onEvent(parsed);
        boundary = eventBoundary(buffer);
      }
      if (done) return;
    }
  } finally {
    signal.removeEventListener("abort", cancel);
    reader.releaseLock();
  }
}

function eventBoundary(
  value: string,
): { index: number; length: number } | null {
  const match = /\r?\n\r?\n/.exec(value);
  return match?.index === undefined
    ? null
    : { index: match.index, length: match[0].length };
}

function parseSseBlock(block: string): PlayerAIEvent | null {
  let id: number | null = null;
  let eventType = "message";
  const data: string[] = [];
  for (const line of block.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    const separator = line.indexOf(":");
    const field = separator === -1 ? line : line.slice(0, separator);
    let value = separator === -1 ? "" : line.slice(separator + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "id" && /^\d+$/.test(value)) id = Number(value);
    else if (field === "event") eventType = value;
    else if (field === "data") data.push(value);
  }
  if (data.length === 0) return null;
  const parsed = JSON.parse(data.join("\n")) as unknown;
  return {
    id,
    eventType,
    data:
      typeof parsed === "object" && parsed !== null
        ? (parsed as Record<string, unknown>)
        : { value: parsed },
  };
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
